/* ==========================================================================
   SkinTalk.mx - Search Form JavaScript
   Custom element <search-form>: debounced predictive search,
   keyboard navigation, results rendering.
   ========================================================================== */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-search-input]');
      this.resultsContainer = this.querySelector('[data-search-results]');
      this.abortController = null;
      this.selectedIndex = -1;
      this.cachedResults = {};
    }

    connectedCallback() {
      if (!this.input || !this.resultsContainer) return;

      this.debouncedSearch = SkinTalk.debounce(this.performSearch.bind(this), 300);

      this.input.addEventListener('input', () => {
        const query = this.input.value.trim();
        if (query.length >= 2) {
          this.debouncedSearch(query);
        } else {
          this.closeResults();
        }
      });

      this.input.addEventListener('keydown', this.onKeyDown.bind(this));

      // Close results when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeResults();
        }
      });

      // Prevent form submit when navigating results
      const form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          const selectedItem = this.resultsContainer.querySelector('[aria-selected="true"]');
          if (selectedItem) {
            e.preventDefault();
            window.location.href = selectedItem.href;
          }
        });
      }
    }

    async performSearch(query) {
      // Check cache first
      if (this.cachedResults[query]) {
        this.renderResults(this.cachedResults[query], query);
        return;
      }

      // Cancel previous request
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      try {
        const url = `${SkinTalk.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=6`;

        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          // Fallback: try simple search
          await this.fallbackSearch(query);
          return;
        }

        const data = await response.json();

        // Cache the result
        this.cachedResults[query] = data;

        this.renderResults(data, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[SkinTalk] Search error:', error);
          await this.fallbackSearch(query);
        }
      }
    }

    async fallbackSearch(query) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
        const response = await fetch(url);

        if (!response.ok) {
          this.renderNoResults(query);
          return;
        }

        const data = await response.json();
        this.renderResults(data, query);
      } catch {
        this.renderNoResults(query);
      }
    }

    renderResults(data, query) {
      const resources = data.resources?.results;
      if (!resources) {
        this.renderNoResults(query);
        return;
      }

      let html = '';
      let hasResults = false;

      // Products
      const products = resources.products || [];
      if (products.length > 0) {
        hasResults = true;
        html += '<div class="header-search__results-group">';
        html += '<div class="header-search__results-heading">Productos</div>';
        products.forEach((product, index) => {
          const image = product.image
            ? `<div class="header-search__result-image"><img src="${this.resizeImage(product.image, '80x80')}" alt="" width="40" height="40" loading="lazy"></div>`
            : '';
          const price = product.price
            ? `<span class="header-search__result-price">${SkinTalk.formatMoney(product.price)}</span>`
            : '';

          html += `
            <a href="${product.url}" class="header-search__result-item" role="option" data-result-index="${index}" aria-selected="false">
              ${image}
              <div class="header-search__result-info">
                <span class="header-search__result-title">${this.highlightMatch(product.title, query)}</span>
                ${price}
              </div>
            </a>
          `;
        });
        html += '</div>';
      }

      // Collections
      const collections = resources.collections || [];
      if (collections.length > 0) {
        hasResults = true;
        html += '<div class="header-search__results-group">';
        html += '<div class="header-search__results-heading">Colecciones</div>';
        collections.forEach((collection) => {
          html += `
            <a href="${collection.url}" class="header-search__result-item" role="option" aria-selected="false">
              <div class="header-search__result-info">
                <span class="header-search__result-title">${this.highlightMatch(collection.title, query)}</span>
                <span class="header-search__result-type">Coleccion</span>
              </div>
            </a>
          `;
        });
        html += '</div>';
      }

      // Articles
      const articles = resources.articles || [];
      if (articles.length > 0) {
        hasResults = true;
        html += '<div class="header-search__results-group">';
        html += '<div class="header-search__results-heading">Articulos</div>';
        articles.forEach((article) => {
          html += `
            <a href="${article.url}" class="header-search__result-item" role="option" aria-selected="false">
              <div class="header-search__result-info">
                <span class="header-search__result-title">${this.highlightMatch(article.title, query)}</span>
                <span class="header-search__result-type">Blog</span>
              </div>
            </a>
          `;
        });
        html += '</div>';
      }

      if (!hasResults) {
        this.renderNoResults(query);
        return;
      }

      // View all link
      html += `<a href="/search?q=${encodeURIComponent(query)}&type=product" class="header-search__view-all">Ver todos los resultados</a>`;

      this.resultsContainer.innerHTML = html;
      this.resultsContainer.classList.add('has-results');
      this.selectedIndex = -1;
    }

    renderNoResults(query) {
      this.resultsContainer.innerHTML = `
        <div class="header-search__no-results">
          No se encontraron resultados para "${this.escapeHtml(query)}"
        </div>
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="header-search__view-all">Buscar "${this.escapeHtml(query)}"</a>
      `;
      this.resultsContainer.classList.add('has-results');
    }

    closeResults() {
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.classList.remove('has-results');
      this.selectedIndex = -1;
    }

    /* ------- Keyboard Navigation ------- */
    onKeyDown(e) {
      const items = this.resultsContainer.querySelectorAll('[role="option"]');
      if (items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
          this.updateSelection(items);
          break;

        case 'ArrowUp':
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.updateSelection(items);
          break;

        case 'Enter':
          if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
            e.preventDefault();
            window.location.href = items[this.selectedIndex].href;
          }
          break;

        case 'Escape':
          this.closeResults();
          this.input.blur();
          break;
      }
    }

    updateSelection(items) {
      items.forEach((item, index) => {
        item.setAttribute('aria-selected', String(index === this.selectedIndex));
      });

      if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
        items[this.selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }

    /* ------- Utility ------- */
    highlightMatch(text, query) {
      if (!query) return this.escapeHtml(text);
      const escaped = this.escapeHtml(text);
      const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi');
      return escaped.replace(regex, '<strong>$1</strong>');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }

    escapeRegExp(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    resizeImage(url, size) {
      if (!url) return '';
      // Shopify image URL resize
      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(\.\w+)(\?|$)/, `_${size}$1$2`);
      }
      return url;
    }
  }

  customElements.define('search-form', SearchForm);
}
